import { Injectable } from '@angular/core';
import { AnimationController, ModalController, ModalOptions } from '@ionic/angular';
import { ScreenAnimationProvider } from './screen-animation-provider';
import { ThemeService, ThemeStyle } from './theme.service';

@Injectable({ providedIn: 'root' })
export class ModalProvider {
  constructor(
    private modalController: ModalController,
    private animationCtrl: AnimationController,
    private themeService: ThemeService,
    private screenAnimationProvider: ScreenAnimationProvider
  ) {}

  async openModal(options: ModalOptions<any>) {
    const modal = await this.modalController.create({
      ...options,
      cssClass:
        'fullscreen-modal' + (this.themeService.current === ThemeStyle.light ? ' light-theme' : ''),
      enterAnimation: this.enterModalAnimation,
      leaveAnimation: this.leaveModalAnimation,
    });

    this.screenAnimationProvider.swipeLeft();

    modal.onWillDismiss().then(() => {
      this.screenAnimationProvider.restoreSwipeLeft();
    });

    modal.present();

    return modal;
  }

  async openCard(options: ModalOptions<any>, initialBreakpoint = 0.5, breakpoints?: number[]) {
    const modal = await this.modalController.create({
      ...options,
      cssClass: 'with-radius',
      breakpoints: breakpoints ?? [0, initialBreakpoint, 1],
      initialBreakpoint: initialBreakpoint,
    });

    const tempThemesData = this.themeService.themes;
    this.screenAnimationProvider.swipeDown();
    this.themeService.changeStylePropertyValue('header_transparent', 'transparent');
    this.themeService.changeStylePropertyValue('tabs_transparent', 'transparent');

    modal.onWillDismiss().then(() => {
      this.screenAnimationProvider.restoreSwipeDown();

      const tabsColor = tempThemesData
        .find((t) => t.type === this.themeService.current)!
        .colors.find((x) => x.key === 'tabs_transparent')!.value;
      this.themeService.changeStylePropertyValue('tabs_transparent', tabsColor);
    });

    modal.onDidDismiss().then(() => {
      const headerColor = tempThemesData
        .find((t) => t.type === this.themeService.current)!
        .colors.find((x) => x.key === 'header_transparent')!.value;
      this.themeService.changeStylePropertyValue('header_transparent', headerColor);
    });

    modal.present();

    return modal;
  }

  async dismiss(data?: any, role?: string, id?: string) {
    await this.modalController.dismiss(data, role, id);
  }

  async dismissTop() {
    var modal = await this.modalController.getTop();
    if (modal) {
      await modal.dismiss();
    }
  }

  async getTop() {
    return this.modalController.getTop();
  }

  private enterModalAnimation = (baseEl: HTMLElement) => {
    const root = baseEl.shadowRoot!;
    const wrapperAnimation = this.animationCtrl
      .create()
      .addElement(root.querySelector('.modal-wrapper')!)
      .fromTo('opacity', '0', '1')
      .fromTo('transform', 'translateX(100px)', 'translateX(0px)');

    return this.animationCtrl
      .create()
      .addElement(baseEl)
      .easing('cubic-bezier(0.175, 0.185, 0.32, 1.08)')
      .duration(100)
      .addAnimation([wrapperAnimation]);
  };

  private leaveModalAnimation = (baseEl: HTMLElement) => {
    return this.enterModalAnimation(baseEl).direction('reverse');
  };
}
